import { Chip, LinearProgress, Paper } from '@shared/ui';
import { deepMerge } from '@shared/utils/merge';
import type { CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import { registerWidgetRenderer } from '../registry';
import type {
  DeepPartial,
  MessageEntity,
  MessageWidgetStyles,
  TimelineWidgetProps,
} from '../types';

export interface MessageWidgetProps extends TimelineWidgetProps<MessageEntity> {
  debugMode?: boolean;
  hideRoleLabel?: boolean;
  hideTimestamp?: boolean;
  userBgColor?: string;
  assistantBgColor?: string;
  messageWidgetStyles?: DeepPartial<MessageWidgetStyles>;
}

// Register renderer + visibility
registerWidgetRenderer(
  'message',
  ({
    entity,
    debugMode,
    hideRoleLabels,
    hideTimestamps,
    userBgColor,
    assistantBgColor,
    messageWidgetStyles,
  }) => (
    <MessageWidget
      key={(entity as any).id}
      entity={entity as any}
      debugMode={debugMode}
      hideRoleLabel={hideRoleLabels}
      hideTimestamp={hideTimestamps}
      userBgColor={userBgColor}
      assistantBgColor={assistantBgColor}
      messageWidgetStyles={messageWidgetStyles}
    />
  ),
  { visibility: { normal: true, debug: true } }
);

export function MessageWidget({
  entity,
  debugMode = false,
  hideRoleLabel = false,
  hideTimestamp = false,
  userBgColor = '#EBF5FF',
  assistantBgColor = '#F5F5F5',
  messageWidgetStyles,
}: MessageWidgetProps) {
  const { role, content, streaming, metadata, severity } = entity.props;
  const isUser = role === 'user';
  const isSystem = role === 'system';
  const isErrorSystemMessage = isSystem && severity === 'error';
  const roleLabel = isUser ? 'You' : isSystem ? 'System' : 'Assistant';

  const defaultStyles: MessageWidgetStyles = {
    user: {
      backgroundColor: userBgColor,
      textColor: undefined,
      codeBlockBgColor: '#1f2937',
      codeBlockTextColor: '#ffffff',
      inlineCodeBgColor: '#e5e7eb',
      inlineCodeTextColor: undefined,
    },
    assistant: {
      backgroundColor: assistantBgColor,
      textColor: undefined,
      codeBlockBgColor: '#1f2937',
      codeBlockTextColor: '#ffffff',
      inlineCodeBgColor: '#e5e7eb',
      inlineCodeTextColor: undefined,
    },
  };

  const styles = deepMerge(defaultStyles, messageWidgetStyles);
  const roleStyles = isUser ? styles.user : styles.assistant;
  const bgColor = isErrorSystemMessage ? '#FEF2F2' : roleStyles.backgroundColor;
  const textColor = isErrorSystemMessage ? '#991B1B' : roleStyles.textColor;

  const text = typeof content === 'string' ? content : '';
  const hasText = text.trim().length > 0;

  // Hide assistant bubbles that contain no visible text (but show them while streaming).
  const isEffectivelyEmpty = !isUser && !hasText && !streaming;

  if (isEffectivelyEmpty) {
    return null;
  }

  const isTransparent = !bgColor || bgColor === 'transparent' || bgColor === 'none';

  const textContent = (
    <div
      className="prose prose-sm max-w-none mp-block artifact-content"
      style={{
        fontSize: '0.9375rem',
        fontWeight: 500,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        ...(textColor && { color: textColor }),
      }}
    >
      <ReactMarkdown
        components={{
          p: ({ children }) => (
            <p className="mb-2 last:mb-0 mt-0" style={textColor ? { color: textColor } : undefined}>
              {children}
            </p>
          ),
          code: ({ children, className }) => {
            const isInline = !className;
            const inlineBg = roleStyles.inlineCodeBgColor || '#e5e7eb';
            const inlineText = roleStyles.inlineCodeTextColor || undefined;
            return isInline ? (
              <code
                className="px-1 py-0.5 rounded text-sm font-mono"
                style={{
                  backgroundColor: inlineBg,
                  ...(inlineText && { color: inlineText }),
                }}
              >
                {children}
              </code>
            ) : (
              <code className={className}>{children}</code>
            );
          },
          pre: ({ children }) => {
            const codeBg = roleStyles.codeBlockBgColor || '#1f2937';
            const codeText = roleStyles.codeBlockTextColor || '#ffffff';
            return (
              <div className="overflow-x-auto max-w-full my-2">
                <pre
                  className="p-3 rounded overflow-x-auto whitespace-pre"
                  style={{ backgroundColor: codeBg, color: codeText }}
                >
                  {children}
                </pre>
              </div>
            );
          },
          ul: ({ children }) => (
            <ul
              className="list-disc ml-4 mb-2"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className="list-decimal ml-4 mb-2"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li
              className="mb-1"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </li>
          ),
          strong: ({ children }) => (
            <strong
              style={{
                fontWeight: 700,
                ...(roleStyles.textColor && { color: roleStyles.textColor }),
              }}
            >
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em
              className="italic"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </em>
          ),
          h1: ({ children }) => (
            <h1
              className="text-xl font-bold mb-2"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              className="text-lg font-bold mb-2"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              className="text-base font-bold mb-1"
              style={roleStyles.textColor ? { color: roleStyles.textColor } : undefined}
            >
              {children}
            </h3>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );

  const streamingProgressStyle: CSSProperties = {
    flexGrow: 1,
    maxWidth: 160,
    ...(roleStyles.streamingProgressTrackColor && {
      ['--message-widget-streaming-track' as const]: roleStyles.streamingProgressTrackColor,
    }),
    ...(roleStyles.streamingProgressColor && {
      ['--message-widget-streaming-bar' as const]: roleStyles.streamingProgressColor,
    }),
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} w-full min-w-0`}>
      <div className="flex flex-col gap-1.5 min-w-0" style={{ maxWidth: '80%' }}>
        {isTransparent ? (
          <div className="min-w-0 mp-block artifact-content" style={{ padding: '2px 0' }}>
            {!hideRoleLabel && (
              <span
                style={{
                  display: 'block',
                  fontSize: '0.7rem',
                  color: 'rgba(0,0,0,0.4)',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                }}
              >
                {roleLabel}
              </span>
            )}
            {textContent}
            {!hideTimestamp && (
              <span
                style={{
                  display: 'block',
                  fontSize: '0.7rem',
                  color: 'rgba(0,0,0,0.35)',
                  marginTop: 4,
                }}
              >
                {new Date(entity.timestamp).toLocaleTimeString()}
              </span>
            )}
          </div>
        ) : (
          <Paper
            elevation={0}
            sx={{
              py: '10px',
              px: '14px',
              bgcolor: bgColor,
              borderRadius: '14px',
              ...(roleStyles.textColor && { color: roleStyles.textColor }),
            }}
            className="min-w-0 mp-block artifact-content"
          >
            {!hideRoleLabel && (
              <span
                style={{
                  display: 'block',
                  fontSize: '0.7rem',
                  color: roleStyles.textColor ? `${roleStyles.textColor}99` : 'rgba(0,0,0,0.4)',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                }}
              >
                {roleLabel}
              </span>
            )}
            {textContent}
            {!hideTimestamp && (
              <span
                style={{
                  display: 'block',
                  fontSize: '0.7rem',
                  color: roleStyles.textColor ? `${roleStyles.textColor}80` : 'rgba(0,0,0,0.35)',
                  marginTop: 4,
                }}
              >
                {new Date(entity.timestamp).toLocaleTimeString()}
              </span>
            )}
          </Paper>
        )}
        {streaming && (
          <div className="flex items-center gap-2 px-1">
            <LinearProgress
              style={streamingProgressStyle}
              sx={{
                ...(roleStyles.streamingProgressTrackColor && {
                  backgroundColor: 'var(--message-widget-streaming-track)',
                }),
                ...(roleStyles.streamingProgressColor && {
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--message-widget-streaming-bar)',
                  },
                }),
              }}
            />
            <span
              style={{
                fontSize: '0.75rem',
                color: roleStyles.streamingIndicatorColor || 'rgba(0,0,0,0.4)',
              }}
            >
              Generating…
            </span>
          </div>
        )}
        {debugMode && metadata && (
          <div className="flex flex-wrap gap-1 px-1">
            {Object.entries(metadata).map(([key, value]) => (
              <Chip key={key} label={`${key}: ${String(value)}`} size="small" variant="outlined" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
